/** Modèle de la fiche héritier (D05) : types lus du contrat et règles d'affichage. */
import { CANAL_INCONNU, CANAUX, STATUTS_PIECE } from './libelles.js';

export type TypeContact = 'phone' | 'whatsapp' | 'email' | 'address';
export type FinaliteConsentement =
  'contact_cabinet' | 'partage_numero_famille' | 'partage_adresse_famille' | 'canal_whatsapp' | 'traitement_donnees';
export type CanalConsentement = 'whatsapp' | 'email' | 'portail' | 'telephone' | 'courrier';

export interface Coordonnee {
  id: string;
  type: TypeContact;
  value: string;
  visibility: 'equipe' | 'notaire' | 'famille';
  maskedForFamily: boolean;
  isPreferred: boolean;
}
export interface PersonneFiche {
  id: string;
  dossierId: string;
  firstNames: string;
  lastName: string;
  birthDate: { value: string | null; precision: 'jour' | 'annee' | 'inconnue' };
  birthPlace: string | null;
  vitalStatus: 'vivant' | 'decede' | 'inconnu';
  relationLabel: string | null;
  role: string;
  reachability: 'joignable' | 'a_relancer' | 'injoignable';
  contacts: Coordonnee[];
  preferences: { preferredChannel: 'whatsapp' | 'email' | 'telephone' | 'courrier' | null; contactOnlyByCabinet: boolean };
  sensitiveLabels: { id: string; label: string; sharedOutsideTeam: boolean }[];
  rowVersion: number;
}
export interface ConsentementLu {
  id: string;
  personId: string;
  purpose: FinaliteConsentement;
  granted: boolean;
  channel: CanalConsentement;
  capturedAt: string;
}
export interface EtiquetteSensible {
  id: string;
  personId: string;
  label: string;
  note: string | null;
  shares: { recipientDisplayName: string; grantedAt: string; revokedAt: string | null }[];
}
export interface PieceAttendue {
  id: string;
  personId: string | null;
  kind: string;
  label: string;
  status: 'attendu' | 'demande' | 'recu' | 'verifie' | 'refuse';
}
export interface PlanRelance {
  id: string;
  personId: string | null;
  cadenceName: string;
  state: 'actif' | 'pause' | 'arrete' | 'termine';
  pausedReason: string | null;
  pausedUntil: string | null;
  nextActionAt: string | null;
  nextAction: { kind: string; channel: string | null; templateCode: string | null } | null;
}
export interface FilEchange {
  id: string;
  personId: string | null;
  channelAccount: { label: string; kind: string; requiresValidation: boolean };
  lastMessageAt: string;
  lastMessagePreview: string;
}
export interface DestinatairePartage {
  userId: string | null;
  role: string | null;
  displayName: string;
}
export interface DonneesFiche {
  personne: PersonneFiche;
  consentements: ConsentementLu[];
  sensibles: EtiquetteSensible[];
  pieces: PieceAttendue[];
  plans: PlanRelance[];
  echanges: FilEchange[];
  destinataires: DestinatairePartage[];
  /** Raison pour laquelle la liste des destinataires n'a pas pu être lue (partage alors impossible), sinon null. */
  partageIndisponible: string | null;
  /** Raison pour laquelle les échanges n'ont pas pu être lus (messagerie indisponible), sinon null. */
  echangesIndisponible: string | null;
}
export interface EtatConsentement {
  etat: 'accorde' | 'refuse' | 'non_recueilli';
  capturedAt: string | null;
  canal: CanalConsentement | null;
}
export interface CorpsPartage {
  recipients: DestinatairePartage[];
  acknowledgedRecipients: string[];
}

const masque = '••••';

/** Masque une coordonnée : l'équipe ne voit la valeur complète qu'après l'avoir demandée. */
export function masquerCoordonnee(type: TypeContact, valeur: string): string {
  if (type === 'phone' || type === 'whatsapp') {
    const chiffres = valeur.replace(/\D/g, '');
    return chiffres.length === 10 ? `${chiffres.slice(0, 4)} •• •• ${chiffres.slice(-2)}` : masque;
  }
  if (type === 'email') {
    const trouve = /^([^@\s])[^@\s]*@([^@\s]+)$/.exec(valeur);
    return trouve ? `${trouve[1]}${masque}@${trouve[2]}` : masque;
  }
  const commune = valeur.includes(',') ? valeur.slice(valeur.lastIndexOf(',') + 1).trim() : '';
  return commune ? `•• , ${commune}` : masque;
}

/** État courant d'une finalité : le consentement daté le plus récent (ajout seul, RG-08). */
export function etatConsentement(liste: readonly ConsentementLu[], finalite: FinaliteConsentement): EtatConsentement {
  let dernier: ConsentementLu | null = null;
  for (const c of liste) {
    if (c.purpose === finalite && (dernier === null || Date.parse(c.capturedAt) > Date.parse(dernier.capturedAt))) dernier = c;
  }
  return dernier
    ? { etat: dernier.granted ? 'accorde' : 'refuse', capturedAt: dernier.capturedAt, canal: dernier.channel }
    : { etat: 'non_recueilli', capturedAt: null, canal: null };
}

export const libelleStatutPiece = (statut: PieceAttendue['status']): string => STATUTS_PIECE[statut] ?? statut;
export const libelleCanal = (canal: string | null): string => (canal !== null && CANAUX[canal]) || CANAL_INCONNU;

/** « 2026-10-02T10:00:00-04:00 » devient « 02/10/2026 » (date locale du cabinet, sans conversion de fuseau). */
export function dateCourte(instant: string): string {
  const [annee, mois, jour] = instant.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

/** Clé d'une case à cocher : l'identifiant de l'utilisateur, à défaut son nom affiché. */
export const cleDestinataire = (d: DestinatairePartage): string => d.userId ?? d.displayName;

/** Les seules personnes cochées ET affichées : une clé inconnue ne désigne personne. */
export const destinatairesCoches = (destinataires: readonly DestinatairePartage[], cles: readonly string[]): DestinatairePartage[] =>
  destinataires.filter((d) => cles.includes(cleDestinataire(d)));

/** Le corps envoyé reprend exactement les noms affichés dans l'alerte (le contrat l'exige). */
export const corpsPartage = (destinataires: readonly DestinatairePartage[]): CorpsPartage => ({
  recipients: destinataires.map((d) => ({ ...d })),
  acknowledgedRecipients: destinataires.map((d) => d.displayName),
});
